import {
	Box,
	Button,
	Card,
	Chip,
	Divider,
	Grid,
	Stack,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import useModal from '@/common/components/ConfirmationDialog/useModal';

import { useNavigate, useParams } from 'react-router-dom';
import {
	DISPLAY_DATE_TIME_FORMAT,
	DISPLAY_DATETIME_12H_FORMAT,
} from '@/utils/dateHelper';
import dayjs from 'dayjs';
import UserInfoForm from './components/UserInfoForm';
import {
	useDeleteUserMutation,
	useGetUserDetailQuery,
} from '@/api/user-management/userManagementListApiSlice';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import { PermissionEnum } from '@/api/enum/common.enum';
import { useGetUser } from '@/hooks/useGetUser';

export default function UserDetailPage() {
	const { t } = useTranslation();
	const showModal = useModal();
	const [deleteUser] = useDeleteUserMutation();
	const { userId } = useParams();
	const navigate = useNavigate();
	const { userInfo } = useGetUser();

	const { data: details } = useGetUserDetailQuery(userId || '', {
		refetchOnMountOrArgChange: true,
	});

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('common.systemAlertMessage')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>{t('common.isDelete')}</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteUser(userId || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteUserSuccess'), {
							variant: 'warning',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						navigate(`/user-management/user-management-list`, {
							replace: true,
						});
					}
				}}
			/>
		));
	};

	const showBranchName = (arr: any) => {
		const nameList = (arr || []).map((item: any) => {
			return item.displayName;
		});
		return nameList.join(', ');
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.systemUserDetail')}
					</Typography>
					{userInfo?.permissionList?.includes(
						PermissionEnum.EditSystemUserAccount
					) && (
						<Stack direction={'row'} alignItems={'center'}>
							<Button
								endIcon={<CreateOutlinedIcon />}
								sx={{ marginRight: '24px' }}
								variant='contained'
								onClick={() => {
									navigate(
										`/user-management/user-management-list/edit/${userId}`
									);
								}}
							>
								{t('button.edit')}
							</Button>
						</Stack>
					)}
				</Box>

				<Card
					sx={{
						padding: ' 24px 24px 0',
						marginTop: 3,
					}}
				>
					<Grid container spacing={3} marginTop={0} marginBottom={3}>
						<Grid size={6}>
							<AppFieldView
								label={t('user.role')}
								value={details?.data.roleLongName}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('user.unit')}
								value={showBranchName(details?.data.branchList)}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('user.userId')}
								value={details?.data.username}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('user.email')}
								value={details?.data.email}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView label={t('user.name')} value={details?.data.name} />
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('user.existInAD')}
								value={details?.data.existInAD ? 'Yes' : 'No'}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.post')}
								value={details?.data.post}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('member.status')}
								value={
									details?.data.disabled ? t('user.suspended') : t('user.active')
								}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('user.creationDateTime')}
								value={
									!details?.data.createdAt
										? '-'
										: dayjs(details?.data.createdAt)
												.locale('en')
												.format(DISPLAY_DATE_TIME_FORMAT)
								}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('user.lastOnlineDateTime')}
								value={
									!details?.data.lastLogin
										? '-'
										: dayjs(details?.data.lastLogin)
												.locale('en')
												.format(DISPLAY_DATE_TIME_FORMAT)
								}
							/>
						</Grid>
					</Grid>
				</Card>
			</Box>
		</>
	);
}
